import { Lens } from '@dhmk/zustand-lens';

import { PreviousYearsAudit } from '@/api/fund/types';
import { FundStructure } from '@/pages/setup-fund/constants/fund-structure';
import { FundInvestmentCompaniesActStatus } from '@/pages/setup-fund/constants/investment-companies-act-status';
import {
  codesForStep,
  firstSubStepIndexOfStep,
  isSubStepApplicable,
  stepForSubStepIndex,
  SUB_STEP_ORDER,
  TOP_LEVEL_STEPS,
} from '@/pages/setup-fund/sub-step-order';
import {
  SUB_STEP_VALIDATORS,
  type ValidationContext,
} from '@/pages/setup-fund/utils/sub-step-validators';
import { FundPresentationType } from '@/pages/setup-fund/utils/type';

export type Shareholder = {
  id: string;
  type: string | null;
  fullName: string | null;
  email: string | null;
  numberOfShares: number | null;
  percentage: number | null;
};

export type ShareClass = {
  id: string;
  className: string | null;
  shares: number | null;
  shareholders: Shareholder[];
};

export type Exemption = {
  type: string | null;
  criteria: string | null;
  maxInvestors: number | null;
  applicable: boolean | null;
};

export type AccountingType = 'Fifo' | 'Lifo' | 'ProRata';

export type FeederDocBundle = {
  notes: string | null;
  documentIds: string[] | null;
};

export type FeederInvestorEligibility = {
  allowed: string[];
  partiallyRestricted: { country: string | null; states: string[] }[];
};

export type Feeder = {
  /** Server-side id; null for newly added feeders not yet saved. */
  id: string | null;
  /** Client-only stable id for React keys + cross-feeder addressing. */
  uid: string;
  name: string | null;
  legalStructure: string | null;
  jurisdictionCountry: string | null;
  jurisdictionState: string | null;
  assignedCountries: string[];
  offeringDocuments: FeederDocBundle;
  subscriptionDocuments: FeederDocBundle;
  riskDisclosure: FeederDocBundle;
  numberUnits: { shareClasses: ShareClass[] };
  subscriptionOptions: StepForms['subscriptionOptions'];
  investorEligibility: FeederInvestorEligibility;
  investmentCompaniesActStatus: FundInvestmentCompaniesActStatus | null;
  previousYearsAudits: PreviousYearsAudit[];
};

/** Default numberUnits with one empty share class — sets the form to an "opened" state in FE by default */
export const getDefaultNumberUnits = (): StepForms['numberUnits'] => ({
  shareClasses: [
    {
      id: crypto.randomUUID(),
      className: null,
      shares: null,
      shareholders: [
        {
          id: crypto.randomUUID(),
          type: null,
          fullName: null,
          email: null,
          numberOfShares: null,
          percentage: null,
        },
      ],
    },
  ],
});

export const getDefaultFeeder = (): Feeder => ({
  id: null,
  uid: crypto.randomUUID(),
  name: null,
  legalStructure: null,
  jurisdictionCountry: null,
  jurisdictionState: null,
  assignedCountries: [],
  offeringDocuments: { notes: null, documentIds: null },
  subscriptionDocuments: { notes: null, documentIds: null },
  riskDisclosure: { notes: null, documentIds: null },
  numberUnits: getDefaultNumberUnits(),
  subscriptionOptions: {
    subscriptionMethod: null,
    wireInformation: {
      bankDetails: {
        name: null,
        address: null,
        city: null,
        country: null,
        state: null,
        zipCode: null,
      },
      beneficiaryDetails: {
        name: null,
        address: null,
        accountNumber: null,
        bankIdentifierCode: null,
        city: null,
        country: null,
        state: null,
        zipCode: null,
        notes: null,
        ABARouting: null,
      },
    },
  },
  investorEligibility: { allowed: [], partiallyRestricted: [] },
  investmentCompaniesActStatus: null,
  previousYearsAudits: [],
});

export type StepForms = {
  fundApprovalStatus: string | null;
  entityId: string | null;
  id: string | null;
  name: string | null;
  description: string | null;
  fundStructure: FundStructure | null;
  feeders: Feeder[];
  /** Section 10 — temporary holding list for countries not yet placed (Master-Feeder only). */
  unassignedCountries: string[];

  investmentDescription: {
    website: string | null;
    name: string | null;
    category: string | null;
    sizeInMillionsUsd: string | null;
    aumInMillionsUsd: string | null;
    description: string | null;
    assets: string[] | null;
    stage: string[] | null;
    strategies: string[] | null;
    legalStructure: string | null;
    jurisdictionCountry: string | null;
    jurisdictionState: string | null;
  };
  feesData: {
    description: string | null;
    fees: {
      feeType: string | null;
      type: string | null;
      notes: string | null;
      percentage: number | null;
      frequency: string | null;
    }[];
  };
  minInvestmentAmounts: {
    currency: string | null;
    additionalDescription: string | null;
    lines: {
      description: string | null;
      value: string | number | null;
      notes: string | null;
      notApplicable: boolean | null;
    }[];
  };
  redemption: {
    conditions: string | null;
    lockUpPeriod: string[] | null;
    frequency: string | null;
    priority: string | null;
    noticeInDays: number | null;
    max: number | null;
    gates: number | null;
    months: number | null;
    noRedemptionAllowed: boolean | null;
    noRedemptionGates: boolean;
  };
  participants: {
    id: string | null;
    name: string | null;
    role: string | null;
    title: string | null;
    updatedAt: string | null;
    createdAt: string | null;
    logoImageId: string | null;
    website: string | null;
  }[];
  invitations: {
    id: string;
    email: string;
    role: 'Director';
    title: string;
    status: 'Pending';
    updatedAt: string;
    createdAt: string;
  }[];
  duration: {
    additionalInfo: string | null;
    isOpenEnded: boolean;
    isNewFund: boolean;
    durationInYears: number | null;
    startDate: string | null;
  };
  accountingType: AccountingType | null;
  returnProfile: {
    projectedReturnProfile: string | null;
    feeType: string | null;
    targetReturnRate: string | null;
    strikeFrequency: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Annually' | null;
    navData: {
      inceptionISO: string | null;
      preferredDataSource: 'file' | 'form' | 'merge' | null;
      uploadedFile: File | null;
      formValues: Record<string, number | undefined>;
      fileValues: Record<string, number>;
    };
  };
  distributionPeriod: {
    additionalInfo: string | null;
    frequency: number | null;
    period: string | null;
    type: string | null;
    frequencyNotApplicable: boolean;
  };
  reportingPeriod: {
    period: string | null;
    reportingNotApplicable: boolean | null;
    additionalInfo: string | null;
  };
  investorEligibility: {
    filter: string | null;
    allowed: string[];
    notAllowed: string[];
    partiallyRestricted: {
      country: string | null;
      states: string[];
    }[];
  };
  countrySpecificExemptions: Record<string, string[]>;
  investmentCompaniesActStatus: FundInvestmentCompaniesActStatus | null;
  offeringDocuments: {
    notes: string | null;
    documentIds: string[] | null;
  };
  subscriptionDocuments: {
    notes: string | null;
    documentIds: string[] | null;
  };
  riskDisclosure: {
    notes: string | null;
    documentIds: string[] | null;
  };
  marketingMaterial: {
    notes: string | null;
    documentIds: string[] | null;
  };
  previousYearsAudits: PreviousYearsAudit[];
  coverImageId: string | null;
  cardImageId: string | null;
  logoId: string | null;
  investmentAvailabilityStatus: string | null;
  transferability: string | null;
  numberUnits: {
    shareClasses: ShareClass[];
  };
  subscriptionOptions: {
    subscriptionMethod: string | null;
    wireInformation: {
      bankDetails: {
        name: string | null;
        address: string | null;
        city: string | null;
        country: string | null;
        state: string | null;
        zipCode: string | null;
      };
      beneficiaryDetails: {
        name: string | null;
        address: string | null;
        accountNumber: string | null;
        bankIdentifierCode: string | null;
        city: string | null;
        country: string | null;
        state: string | null;
        zipCode: string | null;
        notes: string | null;
        ABARouting: string | null;
      };
    } | null;
  };
  howFundWorks: {
    whatItInvestsIn: string | null;
    howItAimsToMakeMoney: string | null;
    whatCanGoWrong: string | null;
  };
  fundPresentation: {
    presentationType: FundPresentationType | null;
    videoUrl: string | null;
    deckFileId: string | null;
  };
  fundBenefits: {
    title: string | null;
    description: string | null;
  }[];
  fundTargetInvestor: {
    idealInvestorProfile: string[];
    whoItsNotFor: string[];
  };
  managerAlignment: {
    gpCapitalInvested: number | null;
    alignmentDescription: string | null;
  };
  faq: {
    question: string | null;
    answer: string | null;
  }[];
};

/** Keys of `StepForms` holding a field group. Lists are excluded: a key patch cannot express a list edit. */
type SectionKey = {
  [K in keyof StepForms]: StepForms[K] extends unknown[]
    ? never
    : StepForms[K] extends object
      ? K
      : never;
}[keyof StepForms];

export type SetupFundSlice = {
  currentStep: number;
  currentSubStep: number;
  entityId: string | null;
  stepForms: StepForms;
  draftStepForms: StepForms;
  approvedStepForms: StepForms | null;
  savedDraftStepForms: StepForms | null;
  lastAppliedResponseId: string | null;
  validatedSteps: number[];
  validatedSubSteps: string[];
  isPendingReview: boolean;
  isViewingNewVersion: boolean;
  changedFieldPaths: string[];
  changedCountByStep: Record<string, number>;
  isBannerLogoUploading: boolean;
  setStep: (_step: number) => void;
  setSubStep: (_index: number) => void;
  updateStepForm: (_data: Partial<StepForms>) => void;
  updateStepFormSection: <K extends SectionKey>(_section: K, _patch: Partial<StepForms[K]>) => void;
  updateReturnProfileNavData: (_data: Partial<StepForms['returnProfile']['navData']>) => void;
  mergeStepFormsPreservingNavData: (_data: Partial<StepForms>) => void;
  resetState: () => void;
  markStepValidated: (_step: number) => void;
  markSubStepValidated: (_code: string) => void;
  seedValidatedSubSteps: (_forms: StepForms, _ctx: ValidationContext) => void;
  setEntityId: (_id: string) => void;
  /** Hydrates the wizard from a fund response. See the implementation for the two rules it obeys. */
  applyFundResponse: (_data: {
    responseId: string;
    draft: StepForms;
    approved: StepForms | null;
    isPendingReview: boolean;
    changedFieldPaths: string[];
    changedCountByStep: Record<string, number>;
  }) => void;
  setIsViewingNewVersion: (_value: boolean) => void;
  setIsBannerLogoUploading: (_value: boolean) => void;
  saveCurrentState: () => void;
  hasUnsavedChanges: () => boolean;
};

export const getInitialStepForms = (): StepForms => ({
  fundApprovalStatus: null,
  entityId: null,
  id: null,
  name: null,
  description: null,
  fundStructure: FundStructure.Standard,
  feeders: [],
  unassignedCountries: [],
  investmentDescription: {
    website: null,
    name: null,
    category: null,
    sizeInMillionsUsd: null,
    aumInMillionsUsd: null,
    strategies: [],
    description: null,
    assets: [],
    stage: null,
    legalStructure: null,
    jurisdictionCountry: null,
    jurisdictionState: null,
  },

  feesData: {
    description: null,
    fees: [{ type: null, feeType: null, notes: null, percentage: null, frequency: null }],
  },
  duration: {
    additionalInfo: null,
    startDate: null,
    isOpenEnded: false,
    isNewFund: false,
    durationInYears: null,
  },
  accountingType: null,
  returnProfile: {
    projectedReturnProfile: null,
    feeType: null,
    targetReturnRate: null,
    strikeFrequency: null,
    navData: {
      inceptionISO: null,
      preferredDataSource: 'merge',
      uploadedFile: null,
      formValues: {},
      fileValues: {},
    },
  },
  redemption: {
    conditions: null,
    lockUpPeriod: null,
    frequency: null,
    priority: null,
    noticeInDays: null,
    max: null,
    gates: null,
    months: null,
    noRedemptionAllowed: false,
    noRedemptionGates: false,
  },
  minInvestmentAmounts: {
    currency: null,
    additionalDescription: null,
    lines: [
      {
        description: 'Minimum Initial Investment',
        value: null,
        notes: null,
        notApplicable: false,
      },
      { description: 'Secondary Minimum', value: null, notes: null, notApplicable: false },
    ],
  },
  distributionPeriod: {
    additionalInfo: null,
    frequency: null,
    period: null,
    type: null,
    frequencyNotApplicable: false,
  },
  reportingPeriod: {
    period: null,
    reportingNotApplicable: false,
    additionalInfo: null,
  },
  investorEligibility: {
    filter: 'Citizenship',
    allowed: [],
    notAllowed: [],
    partiallyRestricted: [],
  },
  countrySpecificExemptions: {},
  investmentCompaniesActStatus: null,
  participants: [],
  invitations: [],
  offeringDocuments: {
    notes: null,
    documentIds: null,
  },
  subscriptionDocuments: {
    notes: null,
    documentIds: null,
  },
  riskDisclosure: {
    notes: null,
    documentIds: null,
  },
  marketingMaterial: {
    notes: null,
    documentIds: null,
  },
  previousYearsAudits: [],
  coverImageId: null,
  cardImageId: null,
  logoId: null,
  investmentAvailabilityStatus: null,
  transferability: null,
  numberUnits: getDefaultNumberUnits(),
  subscriptionOptions: {
    subscriptionMethod: null,
    wireInformation: {
      bankDetails: {
        name: null,
        address: null,
        city: null,
        country: null,
        state: null,
        zipCode: null,
      },
      beneficiaryDetails: {
        name: null,
        address: null,
        accountNumber: null,
        bankIdentifierCode: null,
        city: null,
        country: null,
        state: null,
        zipCode: null,
        notes: null,
        ABARouting: null,
      },
    },
  },
  howFundWorks: {
    whatItInvestsIn: null,
    howItAimsToMakeMoney: null,
    whatCanGoWrong: null,
  },
  fundPresentation: {
    presentationType: null,
    videoUrl: null,
    deckFileId: null,
  },
  fundBenefits: [
    { title: null, description: null },
    { title: null, description: null },
    { title: null, description: null },
    { title: null, description: null },
  ],
  fundTargetInvestor: {
    idealInvestorProfile: [''],
    whoItsNotFor: [''],
  },
  managerAlignment: {
    gpCapitalInvested: null,
    alignmentDescription: null,
  },
  faq: [{ question: null, answer: null }],
});

const initialState: Omit<
  SetupFundSlice,
  | 'setStep'
  | 'resetState'
  | 'updateStepForm'
  | 'updateStepFormSection'
  | 'updateReturnProfileNavData'
  | 'mergeStepFormsPreservingNavData'
  | 'markStepValidated'
  | 'markSubStepValidated'
  | 'seedValidatedSubSteps'
  | 'setSubStep'
  | 'setEntityId'
  | 'applyFundResponse'
  | 'setIsViewingNewVersion'
  | 'setIsBannerLogoUploading'
  | 'saveCurrentState'
  | 'hasUnsavedChanges'
> = {
  currentStep: 1,
  currentSubStep: 0,
  validatedSteps: [],
  validatedSubSteps: [],
  entityId: null,
  approvedStepForms: null,
  savedDraftStepForms: null,
  lastAppliedResponseId: null,
  isPendingReview: false,
  isViewingNewVersion: true,
  changedFieldPaths: [],
  changedCountByStep: {},
  isBannerLogoUploading: false,
  stepForms: getInitialStepForms(),
  draftStepForms: getInitialStepForms(),
};

const validatedStepsFrom = (validatedSubSteps: string[], forms: StepForms): number[] =>
  TOP_LEVEL_STEPS.filter((step) =>
    codesForStep(step)
      .filter((code) => isSubStepApplicable(code, forms))
      .every((code) => validatedSubSteps.includes(code)),
  );

export const createSetupFundStore: Lens<SetupFundSlice> = (set, get) => ({
  ...initialState,
  setSubStep: (index) =>
    set((state) => {
      const step = stepForSubStepIndex(index);
      if (step === undefined) return;
      if (state.draftStepForms.id) {
        state.savedDraftStepForms = JSON.parse(JSON.stringify(state.draftStepForms));
      }
      state.currentSubStep = index;
      state.currentStep = step;
    }),
  setStep: (step) => {
    const index = firstSubStepIndexOfStep(step);
    if (index < 0) return;
    get().setSubStep(index);
  },
  resetState: () =>
    set(() => {
      const draftStepForms = {
        ...initialState.draftStepForms,
        id: null,
      };
      const newState = {
        ...initialState,
        stepForms: {
          ...initialState.stepForms,
          id: null,
        },
        draftStepForms,
        approvedStepForms: null,
        savedDraftStepForms: JSON.parse(JSON.stringify(draftStepForms)) as StepForms,
        isViewingNewVersion: true,
        isPendingReview: false,
        changedFieldPaths: [],
        changedCountByStep: {},
      };
      return newState;
    }),
  updateStepForm: (data) =>
    set((state) => {
      state.draftStepForms = {
        ...state.draftStepForms,
        ...data,
      };
      if (state.isViewingNewVersion) {
        state.stepForms = {
          ...state.stepForms,
          ...data,
        };
      }
    }),
  // Patches within a section. `updateStepForm` merges only at the top level, so writing one
  // field through it means rebuilding the whole section from a snapshot — and a second write
  // racing the first rebuilds from the same stale copy and drops it.
  updateStepFormSection: (section, patch) =>
    set((state) => {
      const merged = { ...(state.draftStepForms[section] as object), ...patch };
      state.draftStepForms = { ...state.draftStepForms, [section]: merged };
      if (state.isViewingNewVersion) {
        state.stepForms = { ...state.stepForms, [section]: merged };
      }
    }),
  updateReturnProfileNavData: (data) =>
    set((state) => {
      state.draftStepForms = {
        ...state.draftStepForms,
        returnProfile: {
          ...state.draftStepForms.returnProfile,
          navData: {
            ...state.draftStepForms.returnProfile.navData,
            ...data,
          },
        },
      };
      if (state.approvedStepForms) {
        state.approvedStepForms = {
          ...state.approvedStepForms,
          returnProfile: {
            ...state.approvedStepForms.returnProfile,
            navData: {
              ...state.approvedStepForms.returnProfile.navData,
              ...data,
            },
          },
        };
      }
      state.stepForms = state.isViewingNewVersion
        ? state.draftStepForms
        : state.approvedStepForms || state.stepForms;
    }),
  mergeStepFormsPreservingNavData: (data) =>
    set((state) => {
      const incomingReturnProfile = (data.returnProfile || {}) as Partial<
        StepForms['returnProfile']
      >;
      const existingNavData = {
        ...state.draftStepForms.returnProfile.navData,
        preferredDataSource:
          incomingReturnProfile.navData?.preferredDataSource ??
          state.draftStepForms.returnProfile.navData.preferredDataSource,
      };
      state.draftStepForms = {
        ...state.draftStepForms,
        ...data,
        returnProfile: {
          ...state.draftStepForms.returnProfile,
          ...incomingReturnProfile,
          navData: existingNavData,
        },
      } as StepForms;
      if (state.isViewingNewVersion) {
        state.stepForms = state.draftStepForms;
      }
    }),
  markStepValidated: (step: number) => {
    if (typeof step !== 'number' || isNaN(step)) return;
    codesForStep(step).forEach((code) => get().markSubStepValidated(code));
  },
  markSubStepValidated: (code: string) =>
    set((state) => {
      if (!state.validatedSubSteps.includes(code)) {
        state.validatedSubSteps.push(code);
      }
      state.validatedSteps = validatedStepsFrom(state.validatedSubSteps, state.draftStepForms);
    }),
  seedValidatedSubSteps: (forms, ctx) =>
    set((state) => {
      SUB_STEP_ORDER.forEach(({ code }) => {
        if (state.validatedSubSteps.includes(code)) return;
        if (SUB_STEP_VALIDATORS[code](forms, ctx)) {
          state.validatedSubSteps.push(code);
        }
      });
      state.validatedSteps = validatedStepsFrom(state.validatedSubSteps, forms);
    }),
  setEntityId: (id: string) =>
    set((state) => {
      state.entityId = id;
    }),
  applyFundResponse: ({
    responseId,
    draft,
    approved,
    isPendingReview,
    changedFieldPaths,
    changedCountByStep,
  }) =>
    set((state) => {
      // Seven components call `useSetupFund`, and each one hydrates from the fund query. Once a
      // response has been applied, a later instance replaying it would restore the fields that
      // Next had already saved — the draft is clean by then, so nothing else would stop it.
      // Remove this once a single owner hydrates the draft.
      const alreadyApplied = state.lastAppliedResponseId === responseId;
      if (alreadyApplied) return;
      state.lastAppliedResponseId = responseId;

      // A response that lands while the user is mid-edit refreshes the review metadata below,
      // but must leave their unsaved work alone.
      const wouldDiscardUnsavedEdits =
        state.draftStepForms.id === draft.id && get().hasUnsavedChanges();

      const preservedNavData = {
        ...state.draftStepForms.returnProfile.navData,
        preferredDataSource:
          draft.returnProfile?.navData?.preferredDataSource ??
          state.draftStepForms.returnProfile.navData.preferredDataSource,
      };

      const nextDraft = JSON.parse(JSON.stringify(draft)) as StepForms;
      nextDraft.returnProfile = {
        ...nextDraft.returnProfile,
        navData: preservedNavData,
      };

      const nextApproved = approved ? (JSON.parse(JSON.stringify(approved)) as StepForms) : null;
      if (nextApproved) {
        nextApproved.returnProfile = {
          ...nextApproved.returnProfile,
          navData: preservedNavData,
        };
      }

      if (!wouldDiscardUnsavedEdits) {
        state.draftStepForms = nextDraft;
        state.savedDraftStepForms = JSON.parse(JSON.stringify(nextDraft));
      }
      state.approvedStepForms = nextApproved;
      state.isPendingReview = isPendingReview;
      state.changedFieldPaths = changedFieldPaths;
      state.changedCountByStep = changedCountByStep;
      if (!isPendingReview) {
        state.isViewingNewVersion = true;
      }
      state.stepForms = state.isViewingNewVersion
        ? state.draftStepForms
        : state.approvedStepForms || state.draftStepForms;
    }),
  setIsViewingNewVersion: (value) =>
    set((state) => {
      state.isViewingNewVersion = value;
      state.stepForms = value
        ? state.draftStepForms
        : state.approvedStepForms || state.draftStepForms;
    }),
  setIsBannerLogoUploading: (value) =>
    set((state) => {
      state.isBannerLogoUploading = value;
    }),
  saveCurrentState: () =>
    set((state) => {
      state.savedDraftStepForms = JSON.parse(JSON.stringify(state.draftStepForms));
    }),
  hasUnsavedChanges: () => {
    const state = get();
    if (!state.savedDraftStepForms) return true;
    return JSON.stringify(state.draftStepForms) !== JSON.stringify(state.savedDraftStepForms);
  },
});
